import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import './BuyInHistory.scss';

const BuyInHistory = ({ buyInHistory = [], playerId, onDeleteBuyIn }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedBuyIn, setSelectedBuyIn] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleMenuOpen = (event, buyIn) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedBuyIn(buyIn);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedBuyIn(null);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    setAnchorEl(null); // Just close the menu but keep selectedBuyIn
  };

  const handleDeleteConfirm = () => {
    if (selectedBuyIn && onDeleteBuyIn) {
      onDeleteBuyIn(playerId, selectedBuyIn.id);
    }
    setDeleteDialogOpen(false);
    setSelectedBuyIn(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedBuyIn(null);
  };

  if (buyInHistory.length === 0) {
    return (
      <Card className="buy-in-history empty">
        <CardContent>
          <Box className="empty-state">
            <HistoryIcon className="empty-icon" />
            <Typography variant="h6" className="empty-title">
              No Buy-ins Yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add a buy-in to start tracking history
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const sortedHistory = [...buyInHistory].sort((a, b) => 
    new Date(b.timestamp) - new Date(a.timestamp)
  );

  return (
    <>
    <Card className="buy-in-history">
      <CardContent>
        <Box className="history-header">
          <HistoryIcon className="history-icon" />
          <Typography variant="h6">
            Buy-in History ({buyInHistory.length})
          </Typography>
        </Box>
        
        <Divider className="header-divider" />
        
        <Box className="history-list">
          {sortedHistory.map((buyIn, index) => (
            <Box key={buyIn.id} className="history-item">
              <Box className="item-header">
                <Box className="item-info">
                  <Chip 
                    label={formatCurrency(buyIn.amount)}
                    color="primary"
                    className="amount-chip"
                  />
                  <Typography variant="body2" color="text.secondary" className="date">
                    {formatDate(buyIn.timestamp)}
                  </Typography>
                </Box>
                {onDeleteBuyIn && (
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, buyIn)}
                    className="menu-button"
                  >
                    <MoreVertIcon />
                  </IconButton>
                )}
              </Box>
              
              {buyIn.note && (
                <Typography variant="body2" className="note">
                  {buyIn.note}
                </Typography>
              )}
              
              {index < sortedHistory.length - 1 && (
                <Divider className="item-divider" />
              )}
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>

    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleMenuClose}
    >
      <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
        <DeleteIcon sx={{ mr: 1 }} />
        Delete Buy-in
      </MenuItem>
    </Menu>

    <Dialog
      open={deleteDialogOpen}
      onClose={handleDeleteCancel}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          <Typography variant="h6">
            Delete Buy-in
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          Are you sure you want to delete this buy-in of{' '}
          <strong>{selectedBuyIn && formatCurrency(selectedBuyIn.amount)}</strong>?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This action cannot be undone and will adjust the player's total buy-in amount.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDeleteCancel} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={handleDeleteConfirm} 
          color="error"
          variant="contained"
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default BuyInHistory;